'use client';

import { cn } from '@module/lib/utils';
import { useQuery } from '@tanstack/react-query';
import type { LottieComponentProps } from 'lottie-react';
import dynamic from 'next/dynamic';

const LazyLoadLottie = dynamic(() => import('lottie-react'), { ssr: false });

interface BaseProps extends Omit<LottieComponentProps, 'animationData' | 'ref' | 'renderer'> {
  animationData?: unknown;
  src?: string;
  className?: string;
  renderer?: 'svg' | 'canvas' | 'html';
}

interface FallbackProps extends BaseProps {
  fallback?: React.ReactNode;
  fallbackThumbnail?: never;
}

interface FallbackThumbnailProps extends BaseProps {
  fallback?: never;
  fallbackThumbnail?: string;
}

type Props = FallbackProps | FallbackThumbnailProps;

const fetchJsonImage = async (url?: string): Promise<Record<string, string>> => {
  if (!url) throw new Error('url is required');
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error('Network response was not ok');
  }
  return res.json();
};

function BaseLottie({ src, animationData, fallback, fallbackThumbnail, className, renderer = 'svg', ...props }: Props) {
  const { data: jsonAnimationData } = useQuery({
    queryKey: ['jsonImage', src],
    queryFn: () => fetchJsonImage(src),
    enabled: !!src && !animationData,
  });
  return (
    <LazyLoadLottie
      animationData={animationData || jsonAnimationData}
      className={cn(className)}
      renderer={renderer as any}
      {...props}
    />
  );
}

export default BaseLottie;
